import { AutosaveEntityType } from '@workspace/shared-constants';
import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { useAutosaveController } from '@/features/autosave/hooks/useAutosaveController';
import { stableStringify } from '@/features/autosave/utils/stableStringify';
import { IApplication, IApplicationFormProps } from '@/types/applications';

const IGNORE_FORM_SYNC_AFTER_SET_MS = 300;
const USER_INPUT_GRACE_MS = 1_000;

type AutosaveSource = 'autosave' | 'baseline' | 'empty';

type UseApplicationAutosaveBridgeParams = {
	baselinePayload: IApplication;
	cloneLoading: boolean;
	formData: IApplication;
	initialEmptyFormData: IApplication;
	mode: IApplicationFormProps['mode'];
	projectId: number | null;
	selectionId: number | null | undefined;
	setFormData: React.Dispatch<React.SetStateAction<IApplication>>;
	setFormResetKey: React.Dispatch<React.SetStateAction<number>>;
};

export const useApplicationAutosaveBridge = ({
	baselinePayload,
	cloneLoading,
	formData,
	initialEmptyFormData,
	mode,
	projectId,
	selectionId,
	setFormData,
	setFormResetKey,
}: UseApplicationAutosaveBridgeParams) => {
	const ignoreFormSyncUntilRef = React.useRef<number>(0);
	const expectedFormFingerprintRef = React.useRef<string | null>(null);
	const userInputUntilRef = React.useRef<number>(0);
	const isChangingSourceRef = React.useRef(false);
	const latestFormDataRef = React.useRef<IApplication>(formData);

	const [showSourceBanner, setShowSourceBanner] = React.useState(false);

	React.useEffect(() => {
		latestFormDataRef.current = formData;
	}, [formData]);

	const markRecentUserInput = React.useCallback(() => {
		userInputUntilRef.current = Date.now() + USER_INPUT_GRACE_MS;
	}, []);

	const autosave = useAutosaveController<IApplication>({
		entityType: AutosaveEntityType.PROJECT_REQUEST,
		projectId,
		selectionId: mode === 'new' ? selectionId ?? null : null,
		entityId: null,
		baselinePayload,
		getPayload: () => formData,
		applyPayload: (payload) => {
			ignoreFormSyncUntilRef.current = Date.now() + IGNORE_FORM_SYNC_AFTER_SET_MS;
			expectedFormFingerprintRef.current = stableStringify(payload);
			latestFormDataRef.current = payload;
			setFormData(payload);
			setFormResetKey((prev) => prev + 1);
		},
		allowEmptySource: mode === 'new',
		loadEmptyPayload: mode === 'new' ? () => cloneDeep(initialEmptyFormData) : undefined,
		enabled: (mode === 'new' || mode === 'project') && !cloneLoading && Boolean(mode === 'new' ? selectionId : projectId),
	});

	const updateFormData = React.useCallback(
		(nextFormData: IApplication) => {
			const nextFormFingerprint = stableStringify(nextFormData);
			const currentFormFingerprint = stableStringify(latestFormDataRef.current);
			const hasRecentUserInput = Date.now() < userInputUntilRef.current;

			if (nextFormFingerprint === currentFormFingerprint) {
				return;
			}

			if (Date.now() < ignoreFormSyncUntilRef.current) {
				if (nextFormFingerprint === expectedFormFingerprintRef.current) {
					latestFormDataRef.current = nextFormData;
					setFormData(nextFormData);
				}

				return;
			}

			expectedFormFingerprintRef.current = null;
			latestFormDataRef.current = nextFormData;
			setFormData(nextFormData);

			if (isChangingSourceRef.current && !hasRecentUserInput) {
				return;
			}

			isChangingSourceRef.current = false;
			setShowSourceBanner(false);
			autosave.updateFromUserInput(nextFormData);
		},
		[autosave.updateFromUserInput, setFormData]
	);

	const changeFormSource = React.useCallback(
		(source: AutosaveSource) => {
			isChangingSourceRef.current = true;
			setShowSourceBanner(true);

			if (source === 'autosave') {
				autosave.useAutosavedVersion();
				return;
			}

			if (source === 'empty') {
				autosave.useEmptyVersion();
				return;
			}

			autosave.useBaselineVersion();
		},
		[autosave.useAutosavedVersion, autosave.useBaselineVersion, autosave.useEmptyVersion]
	);

	return {
		autosave,
		bannerProps: {
			activeSource: autosave.state.activeSource,
			canSwitchSource: showSourceBanner || autosave.state.canSwitchSource,
			draftUpdatedAt: autosave.state.draftUpdatedAt,
			hasAutosaveSource: autosave.availableSources.hasAutosaveSource,
			hasBaselineSource: autosave.availableSources.hasBaselineSource,
			hasEmptySource: autosave.availableSources.hasEmptySource,
			onSwitchSource: changeFormSource,
		},
		formAutosaveProps: {
			onChangeCapture: markRecentUserInput,
			onFormDataChange: updateFormData,
			onInputCapture: markRecentUserInput,
			onKeyDownCapture: markRecentUserInput,
		},
		restoreModalProps: {
			allowEmptySource: mode === 'new',
			draftUpdatedAt: autosave.state.draftUpdatedAt,
			onKeepBaseline: () => changeFormSource('baseline'),
			onRestoreAutosave: () => changeFormSource('autosave'),
			onStartEmpty: mode === 'new' ? () => changeFormSource('empty') : undefined,
			open: autosave.state.restoreModalOpen,
		},
	};
};
